import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useT } from '../../i18n/i18n';
import { useBackHandler } from '../backStack';
import { useOverlayTouchGuard } from '../overlayTouch';
import { useBodyScrollLock } from '../scrollLock';
import { useVisibleViewportFit } from '../visibleViewport';
import { Button } from './Button';
import { Portal } from './Portal';

// Sheets (03 full-screen item sheet · bottom pickers · centered confirm on iPad).
// Every sheet has an explicit close and the backdrop also closes — a modal without an escape
// hatch is forbidden by owner iron rule (OC §9.4). While a sheet is open the page behind it is
// scroll-locked (src/ui/scrollLock.ts); the sheet body is its own scroll container. Every sheet
// renders through the overlay portal (never inside another sheet's DOM) and guards its touches
// (src/ui/overlayTouch.ts) — Round 2 §X, one shared overlay architecture.
export function SheetShell(props: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  closeLabel?: string;
  children: ReactNode;
  footer?: ReactNode;
  // base = over the page · over = over another sheet · top = over the lightbox (below confirms)
  layer?: 'base' | 'over' | 'top';
  full?: boolean;
  testId?: string;
}) {
  const { t } = useT();
  const backdrop = useRef<HTMLDivElement | null>(null);
  const panel = useRef<HTMLElement | null>(null);
  const footerEl = useRef<HTMLElement | null>(null);
  const hasFooter = Boolean(props.footer);
  useBodyScrollLock();
  useOverlayTouchGuard(backdrop);
  // System Back (Android) closes this sheet exactly like its header Close (src/ui/backStack.ts).
  useBackHandler(props.onClose);
  // A bottom sheet stays above the software keyboard, footer and all (iPhone run 2026-10-26 #6;
  // src/ui/visibleViewport.ts). A full-screen sheet keeps its own layout.
  useVisibleViewportFit(backdrop, panel, !props.full);
  // Footer clearance (NY F1 R3 narrow repair, F-R3-03): the footer is sticky over the sheet's own scroll
  // area, so anything scrolled into view — by a sheet revealing what a choice means, or by the browser
  // bringing a focused field on screen — must stop above it. scroll-padding-bottom follows the footer's
  // real height and changes no layout.
  useLayoutEffect(() => {
    const el = panel.current;
    if (!el) return undefined;
    const apply = () => {
      // The sticky footer rests above the sheet's own bottom padding, so the clearance is both. (A sheet
      // with a footer carries that padding inside the footer — theme.css .sheet-has-footer — so the
      // sheet's own is 0 there and the footer's height already includes it.)
      const padding = typeof getComputedStyle === 'function' ? parseFloat(getComputedStyle(el).paddingBottom) || 0 : 0;
      el.style.scrollPaddingBottom = footerEl.current ? `${footerEl.current.offsetHeight + padding}px` : '';
    };
    apply();
    if (!footerEl.current || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(apply);
    observer.observe(footerEl.current);
    return () => observer.disconnect();
  }, [hasFooter]);
  const layerClass = props.layer === 'over' ? 'sheet-backdrop-over' : props.layer === 'top' ? 'sheet-backdrop-topmost' : '';
  return (
    <Portal>
      <div ref={backdrop} className={`sheet-backdrop ${layerClass} ${props.full ? 'sheet-backdrop-full' : ''}`} onClick={props.onClose} data-overlay="sheet">
        <section
          ref={panel}
          className={`sheet ${props.full ? 'sheet-full' : ''} ${hasFooter ? 'sheet-has-footer' : ''}`}
          role="dialog"
          aria-modal="true"
          aria-label={props.title}
          onClick={(event) => event.stopPropagation()}
          data-scroll-container="true"
          data-testid={props.testId}
        >
          <span className="sheet-grip" aria-hidden="true" />
          <header className="sheet-header">
            <div className="sheet-heading">
              <h2 className="sheet-title">{props.title}</h2>
              {props.subtitle ? <p className="sheet-subtitle">{props.subtitle}</p> : null}
            </div>
            <button type="button" className="sheet-close" onClick={props.onClose} data-testid="sheet-close">
              {props.closeLabel ?? t('shell.close')}
            </button>
          </header>
          <div className="sheet-body">{props.children}</div>
          {hasFooter ? <footer ref={footerEl} className="sheet-footer">{props.footer}</footer> : null}
        </section>
      </div>
    </Portal>
  );
}

// Explanation sheet behind a "?" (e.g. what the optional AI area scan does and does not do):
// a title, plain paragraphs, one Close. Never a form, never a decision.
export function InfoSheet(props: { title: string; paragraphs: string[]; onClose: () => void; testId?: string }) {
  const { t } = useT();
  return (
    <SheetShell
      title={props.title}
      onClose={props.onClose}
      layer="over"
      testId={props.testId}
      footer={
        <Button variant="secondary" onClick={props.onClose} testId="info-close">
          {t('shell.close')}
        </Button>
      }
    >
      <div className="info-sheet">
        {props.paragraphs.map((text, index) => (
          <p className="card-body" key={index}>
            {text}
          </p>
        ))}
      </div>
    </SheetShell>
  );
}

// Confirmation for destructive actions: cancel is always first-class and closes without
// side effects (popup-must-have-escape).
export function ConfirmSheet(props: {
  title: string;
  body: string;
  confirmLabel: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  danger?: boolean;
  // An acknowledgement the person must tick before they may confirm (OD-12 / approved draft §5.2:
  // the final-report confirmation is ticked, not just tapped). Cancel is unaffected.
  acknowledge?: string;
  testId?: string;
}) {
  const { t } = useT();
  const [acknowledged, setAcknowledged] = useState(false);
  const backdrop = useRef<HTMLDivElement | null>(null);
  useBodyScrollLock();
  useOverlayTouchGuard(backdrop);
  // System Back (Android) is Cancel — never Confirm (src/ui/backStack.ts).
  useBackHandler(props.onCancel);
  return (
    <Portal>
      <div ref={backdrop} className="sheet-backdrop sheet-backdrop-top" onClick={props.onCancel} data-overlay="confirm">
        <section className="sheet sheet-confirm" role="alertdialog" aria-modal="true" aria-label={props.title} onClick={(event) => event.stopPropagation()} data-scroll-container="true" data-testid={props.testId}>
          <div className="sheet-body">
            <p className="confirm-title">{props.title}</p>
            <p className="confirm-body">{props.body}</p>
            {props.acknowledge ? (
              <label className="confirm-ack">
                <input type="checkbox" checked={acknowledged} onChange={(event) => setAcknowledged(event.target.checked)} data-testid="confirm-ack" />
                <span>{props.acknowledge}</span>
              </label>
            ) : null}
            <div className="confirm-actions">
              <Button variant={props.danger ? 'danger' : 'primary'} size="regular" onClick={props.onConfirm} disabled={props.acknowledge !== undefined && !acknowledged} testId="confirm-yes">
                {props.confirmLabel}
              </Button>
              <Button variant="text" onClick={props.onCancel} testId="confirm-cancel">
                {props.cancelLabel ?? t('shell.cancel')}
              </Button>
            </div>
          </div>
        </section>
      </div>
    </Portal>
  );
}
